import * as React from "react";
import Link from "next/link";
import toast from "react-hot-toast";
import Image from "next/image";
import Icon from "@components/icon";
import { useRouter } from "next/router";
import blurDataUrl from "@utils/shimmer";
import { useMutation } from "urql";
import {
  PinVendorService,
  UnpinVendorService,
} from "@graphql/mutations/vendor.mutation";
import {
  PinVendorServiceMutation,
  PinVendorServiceMutationVariables,
  UnPinVendorServiceMutation,
  UnPinVendorServiceMutationVariables,
} from "@graphql/types";
import useStore from "src/store";
import { motion } from "framer-motion";

type Props = {
  id: string;
  image: string;
  categoryName: string;
  pinned: boolean;
  priority: boolean;
  vendorName: string;
};

const SmallServiceCard: React.FC<Props> = (props) => {
  const [pinState, setPinState] = React.useState(props.pinned);
  const router = useRouter();
  const accessToken = useStore((state) => state.accessToken);

  const [, pin] = useMutation<
    PinVendorServiceMutation,
    PinVendorServiceMutationVariables
  >(PinVendorService);

  const [, unpin] = useMutation<
    UnPinVendorServiceMutation,
    UnPinVendorServiceMutationVariables
  >(UnpinVendorService);

  return (
    <div className="flex flex-col justify-start items-start gap-2 relative w-full min-w-0">
      {pinState ? (
        <div
          onClick={() => {
            if (!!accessToken) {
              setPinState(false);
              toast.success(
                <p className="toast">
                  You removed your interest in {props.vendorName}
                </p>
              );
              unpin({ vendorServiceId: props.id }).catch(() => {
                setPinState(true);
                toast.error(
                  <p className="toast">
                    Failed to remove interest in {props.vendorName}
                  </p>
                );
              });
            } else {
              router.push("/login");
              toast.error(
                <p className="toast">
                  You need to be logged in to show interest in{" "}
                  {props.vendorName}
                </p>
              );
            }
          }}
          className="w-fit p-2 hover:brightness-90 cursor-pointer h-fit z-[2] absolute top-0 right-0"
        >
          <Icon iconName="pinned" />
        </div>
      ) : (
        <div
          onClick={() => {
            if (!!accessToken) {
              setPinState(true);
              toast.success(
                <p className="toast">
                  Your interest in {props.vendorName} has been saved
                </p>
              );
              pin({ vendorServiceId: props.id }).catch(() => {
                setPinState(false);
                toast.error(
                  <p className="toast">Your interest wasn&apos;t saved</p>
                );
              });
            } else {
              router.push("/login");
              toast.error(
                <p className="toast">
                  You need to be logged in to show interest in{" "}
                  {props.vendorName}
                </p>
              );
            }
          }}
          className="w-fit p-2 hover:brightness-90 cursor-pointer h-fit z-[2] absolute top-0 right-0"
        >
          <Icon iconName="notPinned" />
        </div>
      )}
      <Link
        href={`/profile/${props.id}`}
        className="flex-1 flex min-h-0 w-full flex-col group"
      >
        <div className="w-full h-full flex flex-col">
          <div className="relative bg-inawo-green overflow-hidden w-full aspect-square rounded-[10px] flex-shrink-0">
            <motion.div
              className="w-full relative flex justify-center items-center h-full overflow-hidden rounded-[10px]"
              initial={{ scale: 1 }}
              whileHover={{ scale: 1.05 }}
            >
              {props.image === "" ? (
                <div
                  className="w-full h-full flex justify-center items-center bg-inawo-green/80"
                  aria-hidden
                >
                  <Icon iconName="sample" />
                </div>
              ) : (
                <Image
                  src={props.image}
                  fill
                  placeholder="blur"
                  blurDataURL={blurDataUrl(400, 400)}
                  className="object-cover object-center"
                  priority={props.priority}
                  sizes="(max-width: 640px) 28vw, (max-width: 768px) 20vw, 14vw"
                  quality={75}
                  alt={`${props.vendorName} ${props.categoryName} service`}
                />
              )}
            </motion.div>
          </div>
          <p className="line-clamp-1 mt-2 text-xs xs:text-sm font-sfpro font-semibold text-inawo-grey">
            {props.vendorName}
          </p>
        </div>
      </Link>
    </div>
  );
};

export default SmallServiceCard;
